/**
 * The discrete action table.
 *
 * ===========================================================================
 * THE ORDER IS PART OF THE CHECKPOINT FORMAT
 * ===========================================================================
 *
 * A trained policy stores one logit per action INDEX. Nothing in a checkpoint
 * records what index 37 meant, so inserting an entry, reordering a group, or
 * changing a group's sweep silently rewires every trained policy: the network
 * still loads, still runs, and plays like noise. That failure has no symptom
 * other than "the bot got worse", which is indistinguishable from a bad run.
 *
 * So: APPEND ONLY, and only ever at the very end. `ACTION_TABLE_SIGNATURE` is a
 * hash of every entry's controls, pinned by `test/rl/actions.test.ts` against a
 * literal, so any edit to an existing entry fails loudly and immediately.
 *
 * ===========================================================================
 * WHY A LOOKUP TABLE AT ALL
 * ===========================================================================
 *
 * Rocket League's controls are five analog axes and three buttons — a
 * continuous action space. A categorical policy over a fixed table of useful
 * COMBINATIONS needs only a softmax and a log-probability; a continuous one
 * needs a squashed Gaussian with a squash-corrected entropy and per-dimension
 * standard deviations. See the header of ./types: correctness is worth more
 * here than the last few percent of control resolution. This is also what
 * RLGym's default action parser does.
 *
 * ===========================================================================
 * COVERAGE, NOT EXHAUSTIVENESS
 * ===========================================================================
 *
 * 62 entries in eight documented groups. The cross product of all eight
 * controls at three levels each would be thousands of entries, most of them
 * unplayable (full reverse plus boost plus handbrake plus a flip). The groups
 * below cover what is actually played: driving, boosting, powersliding,
 * jumping, the eight flip directions, aerial pitch/yaw, and directional air
 * roll.
 *
 * Entries are deduplicated by construction — the aerial group skips its
 * (pitch 0, yaw 0) rows because those are already the plain throttle and
 * throttle+boost entries, and a duplicated action splits probability mass
 * between two indices that mean the same thing.
 */

import { neutralInput, type CarInput } from '../physics/car-state';
import type { ActionEntry, ActionId } from './types';

// ---------------------------------------------------------------------------
// Construction
// ---------------------------------------------------------------------------

interface Controls {
  throttle?: number;
  steer?: number;
  pitch?: number;
  yaw?: number;
  roll?: number;
  jump?: boolean;
  boost?: boolean;
  handbrake?: boolean;
}

const entries: ActionEntry[] = [];

const push = (label: string, controls: Controls): void => {
  const input: CarInput = { ...neutralInput(), ...controls };
  entries.push({ input: Object.freeze(input), label });
};

/** Steer/axis levels, as text, for a label that reads like a controller. */
const axisLabel = (v: number): string =>
  v === 0 ? '0' : v === 1 ? 'full+' : v === -1 ? 'full-' : v > 0 ? `+${v}` : `${v}`;

// ---------------------------------------------------------------------------
// Group 1 (0..14) — ground driving: throttle x steer
// ---------------------------------------------------------------------------

const THROTTLES: readonly number[] = [1, 0, -1];
const STEERS: readonly number[] = [-1, -0.5, 0, 0.5, 1];

for (const throttle of THROTTLES) {
  for (const steer of STEERS) {
    push(`drive t=${axisLabel(throttle)} s=${axisLabel(steer)}`, { throttle, steer });
  }
}

// ---------------------------------------------------------------------------
// Group 2 (15..19) — ground boost. Throttle is pinned to 1: boosting with any
// other throttle is never what a player means, and the ground boost thrust is
// applied along forward regardless.
// ---------------------------------------------------------------------------

for (const steer of STEERS) {
  push(`boost s=${axisLabel(steer)}`, { throttle: 1, steer, boost: true });
}

// ---------------------------------------------------------------------------
// Group 3 (20..27) — powerslide. Zero steer with the handbrake down is just a
// friction change and is covered by the plain drive entries, so it is omitted.
// ---------------------------------------------------------------------------

const SLIDE_STEERS: readonly number[] = [-1, -0.5, 0.5, 1];

for (const throttle of [1, 0]) {
  for (const steer of SLIDE_STEERS) {
    push(`slide t=${axisLabel(throttle)} s=${axisLabel(steer)}`, {
      throttle,
      steer,
      handbrake: true,
    });
  }
}

// ---------------------------------------------------------------------------
// Group 4 (28..29) — boosted powerslide, the fast-corner action.
// ---------------------------------------------------------------------------

for (const steer of [-1, 1]) {
  push(`boost slide s=${axisLabel(steer)}`, {
    throttle: 1,
    steer,
    boost: true,
    handbrake: true,
  });
}

// ---------------------------------------------------------------------------
// Group 5 (30..33) — jumps with NO stick deflection.
//
// The deflection matters: ../physics/jump treats a second jump press with
// |pitch| + |yaw| + |roll| below DODGE_DEADZONE as a double jump and anything
// above it as a flip. These four are therefore the plain jump / double jump
// actions, and group 6 is the flips.
// ---------------------------------------------------------------------------

for (const steer of [-1, 0, 1]) {
  push(`jump s=${axisLabel(steer)}`, { throttle: 1, steer, jump: true });
}
push('jump + boost', { throttle: 1, jump: true, boost: true });

// ---------------------------------------------------------------------------
// Group 6 (34..41) — the eight flip directions.
//
// ../physics/jump derives the dodge direction as (-pitch, yaw + roll): stick
// forward is NEGATIVE pitch and gives a front flip, and +yaw dodges toward the
// car's `left` column. The labels below follow that mapping.
// ---------------------------------------------------------------------------

const FLIPS: readonly { pitch: number; yaw: number; label: string }[] = [
  { pitch: -1, yaw: 0, label: 'flip front' },
  { pitch: 1, yaw: 0, label: 'flip back' },
  { pitch: 0, yaw: 1, label: 'flip left' },
  { pitch: 0, yaw: -1, label: 'flip right' },
  { pitch: -1, yaw: 1, label: 'flip front-left' },
  { pitch: -1, yaw: -1, label: 'flip front-right' },
  { pitch: 1, yaw: 1, label: 'flip back-left' },
  { pitch: 1, yaw: -1, label: 'flip back-right' },
];

for (const flip of FLIPS) {
  push(flip.label, { throttle: 1, pitch: flip.pitch, yaw: flip.yaw, jump: true });
}

// ---------------------------------------------------------------------------
// Group 7 (42..57) — aerial pitch x yaw, with and without boost.
//
// Throttle stays at 1 because THROTTLE_AIR_ACCELERATION applies airborne too,
// and an aerial with no throttle is strictly worse. (pitch 0, yaw 0) is skipped
// in both boost states: those rows are byte-identical to entries 2 and 17.
// ---------------------------------------------------------------------------

const AIR_AXES: readonly number[] = [-1, 0, 1];

for (const boost of [false, true]) {
  for (const pitch of AIR_AXES) {
    for (const yaw of AIR_AXES) {
      if (pitch === 0 && yaw === 0) continue;
      push(
        `air p=${axisLabel(pitch)} y=${axisLabel(yaw)}${boost ? ' + boost' : ''}`,
        { throttle: 1, pitch, yaw, boost }
      );
    }
  }
}

// ---------------------------------------------------------------------------
// Group 8 (58..61) — air roll, with and without boost.
//
// The plain `roll` axis rather than the handbrake air-roll modifier: the
// modifier maps STEER onto roll (see ../physics/aerial), which would make the
// same physical rotation reachable through two different table entries.
// ---------------------------------------------------------------------------

for (const boost of [false, true]) {
  for (const roll of [-1, 1]) {
    push(`roll ${axisLabel(roll)}${boost ? ' + boost' : ''}`, { throttle: 1, roll, boost });
  }
}

// ---------------------------------------------------------------------------
// The table
// ---------------------------------------------------------------------------

/** The lookup table. Frozen: an accidental in-place edit is a rewired policy. */
export const ACTIONS: readonly ActionEntry[] = Object.freeze(entries);

export const ACTION_COUNT = ACTIONS.length;

/** Group boundaries, for the debug overlay and for the coverage test. */
export const ACTION_GROUPS: readonly { name: string; start: number; end: number }[] = [
  { name: 'drive', start: 0, end: 15 },
  { name: 'boost', start: 15, end: 20 },
  { name: 'powerslide', start: 20, end: 28 },
  { name: 'boost-powerslide', start: 28, end: 30 },
  { name: 'jump', start: 30, end: 34 },
  { name: 'flip', start: 34, end: 42 },
  { name: 'aerial', start: 42, end: 58 },
  { name: 'air-roll', start: 58, end: 62 },
];

/**
 * The controls of one action. The returned object is FROZEN and shared — the
 * world only reads inputs, and handing out a copy per agent per tick would
 * allocate once per step for nothing.
 */
export const actionInput = (id: ActionId): Readonly<CarInput> => {
  const entry = ACTIONS[id];
  if (entry === undefined) throw new RangeError(`action ${id} is outside 0..${ACTION_COUNT - 1}`);
  return entry.input;
};

export const actionLabel = (id: ActionId): string => {
  const entry = ACTIONS[id];
  return entry === undefined ? `<invalid ${id}>` : entry.label;
};

// ---------------------------------------------------------------------------
// Signature
// ---------------------------------------------------------------------------

/** Canonical text for one entry. Order of the fields is part of the hash. */
const canonical = (entry: ActionEntry): string => {
  const i = entry.input;
  return [
    entry.label,
    i.throttle,
    i.steer,
    i.pitch,
    i.yaw,
    i.roll,
    i.jump ? 1 : 0,
    i.boost ? 1 : 0,
    i.handbrake ? 1 : 0,
  ].join(',');
};

/** FNV-1a, 32-bit. Integer arithmetic end to end, so the digest is identical
 *  in every engine — the same reason ../physics/rng is mulberry32. */
const fnv1a = (text: string): number => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
};

/**
 * Hash of the whole table, including labels. Pinned in the tests: if this
 * changes, a trained checkpoint's logits no longer mean what they meant, and
 * the change has to be a deliberate append with a version bump rather than an
 * accident.
 */
export const ACTION_TABLE_SIGNATURE = fnv1a(ACTIONS.map(canonical).join(';'))
  .toString(16)
  .padStart(8, '0');

// ---------------------------------------------------------------------------
// Quantizing a continuous input
// ---------------------------------------------------------------------------

/** Cost of disagreeing with a button. Higher than any axis disagreement can be
 *  on its own, because pressing jump when the reference did not is a different
 *  ACTION, not a slightly different one. */
const JUMP_COST = 4;
const BOOST_COST = 2;
const HANDBRAKE_COST = 1;

/**
 * The table entry closest to an arbitrary `CarInput`, by weighted L1 distance.
 *
 * NOT part of the RL loop — a policy emits an index directly. This exists so a
 * scripted bot's continuous output can be pushed through the same discrete
 * channel the learner uses, which is what makes "a competent player out-scores
 * noise under this reward function" a test of the reward function rather than
 * of the control resolution. It is also what a behaviour-cloning warm start
 * would need.
 *
 * Ties break to the lowest index, so the mapping is a pure function.
 */
export const nearestAction = (input: CarInput): ActionId => {
  let best = 0;
  let bestCost = Number.POSITIVE_INFINITY;
  for (let id = 0; id < ACTIONS.length; id++) {
    const c = ACTIONS[id].input;
    let cost =
      Math.abs(c.throttle - input.throttle) +
      Math.abs(c.steer - input.steer) +
      Math.abs(c.pitch - input.pitch) +
      Math.abs(c.yaw - input.yaw) +
      Math.abs(c.roll - input.roll);
    if (c.jump !== input.jump) cost += JUMP_COST;
    if (c.boost !== input.boost) cost += BOOST_COST;
    if (c.handbrake !== input.handbrake) cost += HANDBRAKE_COST;
    if (cost < bestCost) {
      bestCost = cost;
      best = id;
    }
  }
  return best;
};
